import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { formatters } from '@utils/formattersV2';
import { DownloadButton } from './DownloadButton';
import { parseReportDateToLocaleFormat } from '@utils/dates';
import { TrendEntry } from '@features/campaign-list/configuration/lci3-mini-chart-options';
import { CampaignListEntry } from '@features/campaign-list/slice/schemas';
import { CampaignLink } from './CampaignLink';
import {
  ReportDownloadModal,
  ReportDownloadModalProps,
  isApproved,
} from '@features/reports';
import { TrendChartContainer } from '../TrendChart/TrendChartContainer';
import { REDESIGN_VERSION_CUTOFF } from './paths';
import { CampaignIcon } from './CampaignIcon';
import { DownloadModal } from '@features/reports/subdomains/cpg';

export type LoadableTrendEntry =
  | { status: 'loading' }
  | { status: 'loaded'; data: TrendEntry[] };

export type CampaignListRow = Pick<
  CampaignListEntry,
  | 'campaignId'
  | 'reportId'
  | 'campaignName'
  | 'advertiserName'
  | 'campaignStartDate'
  | 'campaignEndDate'
  | 'impressions'
  | 'visitLift'
  | 'salesLift'
  | 'lciVersion'
  | 'approvalStatus'
  | 'campaignType'
> & {
  reportType: CampaignListEntry['reportType'] | 'custom_recurring'; // TODO: Remove custom_recurring after we no longer support LCI 2.x
  incrementalVisitsTrending: LoadableTrendEntry;
  incrementalSalesTrending: LoadableTrendEntry;
  partner?: string; // TODO: Remove when partner reports are no longer supported on index
  download?: Omit<ReportDownloadModalProps, 'renderOpener'>;
};

export const columns: GridColDef<CampaignListRow>[] = [
  { field: 'campaignId', headerName: 'ID', flex: 0.5, minWidth: 70 },
  {
    field: 'campaignType',
    headerName: 'Type',
    flex: 0.5,
    minWidth: 70,
    sortable: false,
    valueGetter: (_, row) => {
      if (row.campaignType === 'cpg') return 'CPG';
      if (row.campaignType === 'rsl') return 'RSL';
      return `LCI ${row.lciVersion}`;
    },
    renderCell: ({ row }) => <CampaignIcon row={row} />,
  },
  {
    field: 'campaignName',
    headerName: 'Campaign Name',
    flex: 1.5,
    minWidth: 150,
    renderCell: params => <CampaignLink row={params.row} />,
  },
  { field: 'advertiserTeamName', headerName: 'Advertiser', flex: 1 },
  {
    field: 'campaignStartDate',
    headerName: 'Start Date',
    flex: 1,
    valueFormatter: (value: CampaignListRow['campaignStartDate']) =>
      parseReportDateToLocaleFormat(value),
  },
  {
    field: 'campaignEndDate',
    headerName: 'End Date',
    flex: 1,
    valueFormatter: (value: CampaignListRow['campaignEndDate']) =>
      parseReportDateToLocaleFormat(value),
  },
  {
    field: 'impressions',
    headerName: 'Impressions',
    flex: 1,
    valueFormatter: (value: CampaignListRow['impressions']) =>
      formatters.impressions(value),
  },

  {
    field: 'salesLift',
    headerName: 'Incr. Sales Lift',
    flex: 1,
    valueFormatter: (value: CampaignListRow['salesLift']) =>
      formatters.lift(value),
  },
  {
    field: 'incrementalSalesTrending',
    headerName: 'Incr. Sales Trending',
    flex: 1,
    filterable: false,
    sortable: false,
    renderCell: (
      params: GridRenderCellParams<
        CampaignListRow,
        CampaignListRow['incrementalSalesTrending']
      >
    ) => <TrendChartContainer trendEntry={params.value} height="50px" />,
  },
  {
    field: 'visitLift',
    headerName: 'Incr. Visit Lift',
    flex: 1,
    valueFormatter: (value: CampaignListRow['visitLift']) =>
      formatters.lift(value),
  },
  {
    field: 'incrementalVisitsTrending',
    headerName: 'Incr. Visit Trending',
    flex: 1,
    filterable: false,
    sortable: false,
    renderCell: (
      params: GridRenderCellParams<
        CampaignListRow,
        CampaignListRow['incrementalVisitsTrending']
      >
    ) => <TrendChartContainer trendEntry={params.value} height="50px" />,
  },
  {
    field: 'download',
    headerName: '',
    flex: 0.5,
    sortable: false,
    filterable: false,
    renderCell: (
      params: GridRenderCellParams<CampaignListRow, CampaignListRow['download']>
    ) => {
      const {
        value: reportDownloadModalProps,
        row: { lciVersion, campaignType },
      } = params;

      if (!reportDownloadModalProps || !isApproved(params.row.approvalStatus))
        return null;

      if (campaignType === 'cpg')
        return (
          <DownloadModal
            renderOpener={onClick => <DownloadButton onClick={onClick} />}
            campaignId={params.row.campaignId}
            campaignName={params.row.campaignName}
            reportId={params.row.reportId}
            campaignParams={reportDownloadModalProps.getCampaignParams}
            channel={params.row.partner ?? null}
          />
        );

      if (lciVersion < REDESIGN_VERSION_CUTOFF) return null;

      return (
        <ReportDownloadModal
          renderOpener={onClick => <DownloadButton onClick={onClick} />}
          channel={params.row.partner}
          enableSalesDownloads={params.row.salesLift !== null}
          {...reportDownloadModalProps}
        />
      );
    },
  },
];
